// Kanban — fem kolonner, dra-og-slipp, og en tastaturvei som gjør det samme.
//
// ── VALE-KORT ER IKKE VÅRE ──────────────────────────────────────────────────
// Kortene med `kilde: 'vale'` er generert fra `capabilities.ts` og leses fra
// `vale/data/kanban.json`. De er merket visuelt, og `DELETE` på dem svarer 403.
// Derfor TILBYR vi ikke sletteknappen på dem — en knapp som alltid feiler lærer
// brukeren regelen ved å la henne bryte den. Kolonneflytting er derimot lov: den
// lagres som et overlegg, og kortet merkes «avviker fra VALE» så forskjellen er
// synlig i stedet for å forsvinne.
//
// ── TASTATUR ────────────────────────────────────────────────────────────────
// Mellomrom løfter kortet, piltastene flytter, Enter slipper, Escape avbryter.
// Løftemodus framfor «Ctrl+pil»: piltaster alene ruller siden, og Ctrl/Alt+pil
// er opptatt av nettleseren selv. Uten dette er tavla ubrukelig uten mus, og en
// kanban man ikke kan bruke uten mus er en kanban halve staben ikke kan bruke.
import {
  KANBAN_KOLONNER, KOLONNE_NAVN, type KanbanKolonne, type Kort,
} from '@skade/dugnad/prosjekt';
import type { Kontekst } from './kontekst.js';
import type { KanbanSvar, KortEndring, NyttKortKropp } from '../api/typer.js';
import {
  antallPerKolonne, filtrerKort, flyttKort, flyttMedTastatur, grupper, kanSlettes,
  prosjekterI, type Flytting, type PerKolonne,
} from '../lib/kanban-lib.js';
import { normaliserSprint, type SprintVisning } from '../lib/sprint-lib.js';
import { div, felt, h, knapp, merke, p, sett, span, tekstområde, velger } from '../ui/dom.js';
import { ikon } from '../ui/ikoner.js';
import { feilboks, laster, søyle, tomt } from '../ui/tilstand.js';

interface Tilstand {
  kort: Kort[];
  kilder: { vale: number; modul: number };
  advarsler: string[];
  personer: string[];
  sprinter: SprintVisning[];
  /** Sant når `/sprinter` ikke finnes ennå — da skal sprint-velgeren si det. */
  sprinterUtilgjengelig: string | null;
}

export function visKanban(rot: HTMLElement, ctx: Kontekst): void {
  const t: Tilstand = {
    kort: [], kilder: { vale: 0, modul: 0 }, advarsler: [],
    personer: [], sprinter: [], sprinterUtilgjengelig: null,
  };
  let løftet: string | null = null;

  const side = div('side side-bred');
  sett(rot, side);

  const topp = div('side-topp');
  const titler = div('');
  titler.append(h(1, 'side-tittel', 'Kanban'));
  titler.append(p('side-under', 'Kort fra VALE og kort opprettet her, slått sammen. '
    + 'Flytting av et VALE-kort lagres som et overlegg — VALE-fila skrives aldri.'));
  topp.append(titler);

  const søk = felt('Søk', { plassholder: 'tittel, notat, prosjekt …' });
  const prosjektV = velger('Prosjekt', [{ verdi: '', navn: 'Alle prosjekt' }]);
  const kildeV = velger('Kilde', [
    { verdi: '', navn: 'Alle kilder' }, { verdi: 'vale', navn: 'Fra VALE' }, { verdi: 'modul', navn: 'Egne kort' },
  ]);
  const sprintV = velger('Sprint', [{ verdi: '', navn: 'Alle sprinter' }]);
  const filterrad = div('filterrad');
  filterrad.append(søk.rot, prosjektV.rot, kildeV.rot, sprintV.rot);
  filterrad.append(knapp('Nytt kort', () => visNyttKort(), { variant: 'primær', ikon: ikon('pluss') }));
  topp.append(filterrad);
  side.append(topp);

  søk.input.addEventListener('input', () => tegnTavle());
  for (const s of [prosjektV.select, kildeV.select, sprintV.select]) {
    s.addEventListener('change', () => tegnTavle());
  }

  const oversikt = div('kanban-oversikt');
  side.append(oversikt);

  const varselplass = div('');
  side.append(varselplass);

  const nyttplass = div('');
  side.append(nyttplass);

  const tavleplass = div('tavleplass');
  side.append(tavleplass);

  const tastaturhjelp = p('tavle-hjelp',
    'Tastatur: gå til et kort med Tab, trykk mellomrom for å løfte det, flytt med '
    + 'piltastene, slipp med Enter. Escape avbryter.');
  side.append(tastaturhjelp);

  void hentAlt();

  // ══ Henting ═══════════════════════════════════════════════════════════
  async function hentAlt(): Promise<void> {
    sett(tavleplass, laster('Henter tavla …', 5));
    sett(oversikt);
    // Personer og sprinter er tilleggsdata: feiler de, skal tavla likevel vises.
    // Derfor egne kall med egne utfall, ikke ett kall som drar med seg de andre.
    const [kanban, personer, sprinter] = await Promise.all([
      ctx.klient.kanban(),
      ctx.klient.personer(),
      ctx.klient.sprinter(),
    ]);

    if (personer.ok) t.personer = (personer.data?.personer ?? []).map((p2) => p2.id);
    if (sprinter.ok) {
      t.sprinter = (sprinter.data?.sprinter ?? [])
        .map(normaliserSprint)
        .filter((s): s is SprintVisning => s !== null);
      t.sprinterUtilgjengelig = null;
    } else {
      t.sprinter = [];
      t.sprinterUtilgjengelig = sprinter.feil.melding;
    }

    if (!kanban.ok) {
      sett(tavleplass, feilboks(kanban.feil, () => void hentAlt()));
      return;
    }
    lesKanban(kanban.data);
    fyllFiltre();
    tegnOversikt();
    tegnVarsler();
    tegnTavle();
  }

  function lesKanban(data: KanbanSvar | null): void {
    const kolonner = (data?.kolonner ?? {}) as Partial<Record<KanbanKolonne, Kort[]>>;
    t.kort = KANBAN_KOLONNER.flatMap((k) => (Array.isArray(kolonner[k]) ? kolonner[k]! : []));
    t.kilder = data?.kilder ?? { vale: 0, modul: 0 };
    t.advarsler = Array.isArray(data?.advarsler) ? data!.advarsler! : [];
  }

  function fyllFiltre(): void {
    const valgt = prosjektV.select.value;
    sett(prosjektV.select);
    for (const v of [{ verdi: '', navn: 'Alle prosjekt' },
      ...prosjekterI(t.kort).map((pr) => ({ verdi: pr, navn: pr }))]) {
      const o = document.createElement('option');
      o.value = v.verdi; o.textContent = v.navn;
      if (v.verdi === valgt) o.selected = true;
      prosjektV.select.append(o);
    }
    const valgtS = sprintV.select.value;
    sett(sprintV.select);
    for (const v of [{ verdi: '', navn: 'Alle sprinter' },
      ...t.sprinter.map((s) => ({ verdi: s.id, navn: s.navn }))]) {
      const o = document.createElement('option');
      o.value = v.verdi; o.textContent = v.navn;
      if (v.verdi === valgtS) o.selected = true;
      sprintV.select.append(o);
    }
  }

  function tegnOversikt(): void {
    sett(oversikt);
    const synlige = filtrerte();
    const per = grupper(synlige);
    const tall = antallPerKolonne(per);
    // Fremdrift regnes av de SYNLIGE kortene, og det står i etiketten. Ellers
    // ville et filter gitt et tall som ikke stemmer med det man ser.
    const med = synlige.filter((k) => k.kolonne !== 'wontdo');
    const andel = med.length ? tall.done / med.length : 0;

    const boks = div('oversiktboks');
    boks.append(span('oversikt-etikett', 'Arbeid ferdig (av kortene som vises)'));
    const rad = div('oversikt-rad');
    rad.append(søyle(andel, 'arbeid', `${Math.round(andel * 100)} % av kortene er ferdige`));
    rad.append(span('oversikt-tall', `${tall.done} av ${med.length}`));
    boks.append(rad);
    boks.append(span('oversikt-under', `${tall.doing} pågår · ${tall.wontdo} utgår`));
    oversikt.append(boks);

    const kilder = div('oversiktboks');
    kilder.append(span('oversikt-etikett', 'Kilder'));
    const kr = div('oversikt-rad');
    kr.append(merke(`${t.kilder.vale} fra VALE`, 'vale'));
    kr.append(merke(`${t.kilder.modul} egne`));
    const overstyrt = t.kort.filter((k) => k.overstyrt).length;
    if (overstyrt) kr.append(merke(`${overstyrt} avviker fra VALE`, 'gull'));
    kilder.append(kr);
    oversikt.append(kilder);
  }

  function tegnVarsler(): void {
    sett(varselplass);
    for (const a of t.advarsler) {
      const boks = div('advarsel advarsel-mild');
      boks.setAttribute('role', 'status');
      const hode = div('advarsel-hode');
      hode.append(ikon('info'), h(2, 'advarsel-tittel', 'Tavla er ufullstendig'));
      boks.append(hode);
      // Tjenestens egen tekst — f.eks. «fant ikke vale/data/kanban.json».
      boks.append(p('advarsel-linje', a));
      varselplass.append(boks);
    }
  }

  function filtrerte(): Kort[] {
    return filtrerKort(t.kort, {
      søk: søk.input.value,
      prosjekt: prosjektV.select.value || undefined,
      kilde: (kildeV.select.value || undefined) as 'vale' | 'modul' | undefined,
      sprintId: sprintV.select.value || undefined,
    });
  }

  // ══ Tavla ═════════════════════════════════════════════════════════════
  function tegnTavle(): void {
    const synlige = filtrerte();
    const per = grupper(synlige);
    sett(tavleplass);

    if (!t.kort.length) {
      tavleplass.append(tomt('Tavla er tom',
        'Ingen kort fra VALE og ingen egne kort ennå. Lag det første kortet.',
        { ikon: 'kanban', handling: { tekst: 'Nytt kort', gjør: () => visNyttKort() } }));
      return;
    }

    const tavle = div('tavle');
    // Rulling ligger på tavla, ikke på siden: siden skal aldri rulle vannrett.
    tavle.setAttribute('role', 'list');
    tavle.setAttribute('aria-label', 'Kanban-kolonner');
    for (const k of KANBAN_KOLONNER) tavle.append(tegnKolonne(k, per[k], per));
    tavleplass.append(tavle);

    if (synlige.length === 0) {
      tavleplass.append(p('tavle-tomt-filter',
        `Ingen av ${t.kort.length} kort passer filteret. Tøm søket for å se alle.`));
    }
  }

  function tegnKolonne(kolonne: KanbanKolonne, kort: readonly Kort[], per: PerKolonne): HTMLElement {
    const sp = div(`kolonne kolonne-${kolonne}`);
    sp.setAttribute('role', 'listitem');

    const hode = div('kolonne-hode');
    hode.append(span('kolonne-navn', KOLONNE_NAVN[kolonne]));
    hode.append(span('kolonne-tall', String(kort.length)));
    sp.append(hode);

    const liste = div('kolonne-liste');
    liste.setAttribute('role', 'group');
    liste.setAttribute('aria-label', `${KOLONNE_NAVN[kolonne]}, ${kort.length} kort`);

    liste.addEventListener('dragover', (e) => {
      e.preventDefault();
      liste.classList.add('over');
    });
    liste.addEventListener('dragleave', () => liste.classList.remove('over'));
    liste.addEventListener('drop', (e) => {
      e.preventDefault();
      liste.classList.remove('over');
      const id = e.dataTransfer?.getData('text/plain');
      if (!id) return;
      void utfør(flyttKort(per, id, kolonne, indeksVed(liste, e.clientY)));
    });

    if (!kort.length) liste.append(p('kolonne-tom', 'Ingen kort'));
    for (const k of kort) liste.append(tegnKort(k, per));
    sp.append(liste);
    return sp;
  }

  /** Hvor i kolonnen slippet skjedde — midtpunktet på hvert kort avgjør. */
  function indeksVed(liste: HTMLElement, y: number): number {
    const kort = [...liste.querySelectorAll<HTMLElement>('.kort-brikke')];
    for (const [i, el] of kort.entries()) {
      const r = el.getBoundingClientRect();
      if (y < r.top + r.height / 2) return i;
    }
    return kort.length;
  }

  function tegnKort(k: Kort, per: PerKolonne): HTMLElement {
    const brikke = div(`kort-brikke kilde-${k.kilde}${løftet === k.id ? ' løftet' : ''}`);
    brikke.tabIndex = 0;
    brikke.draggable = true;
    brikke.dataset.id = k.id;
    brikke.setAttribute('role', 'button');
    brikke.setAttribute('aria-roledescription', 'flyttbart kort');
    brikke.setAttribute('aria-label',
      `${k.title}. ${KOLONNE_NAVN[k.kolonne]}. ${k.kilde === 'vale' ? 'Kort fra VALE.' : 'Eget kort.'}`
      + (k.overstyrt ? ' Avviker fra VALE.' : ''));

    brikke.addEventListener('dragstart', (e) => {
      e.dataTransfer?.setData('text/plain', k.id);
      brikke.classList.add('drar');
    });
    brikke.addEventListener('dragend', () => brikke.classList.remove('drar'));
    brikke.addEventListener('keydown', (e) => tastetrykk(e, k, per, brikke));

    const topp2 = div('kort-topp');
    topp2.append(span('kort-tittel', k.title));
    if (k.kilde === 'vale') {
      const v = merke('VALE', 'vale');
      v.prepend(ikon('vale'));
      topp2.append(v);
    }
    brikke.append(topp2);

    if (k.note) brikke.append(p('kort-notat', k.note));

    const merker = div('kort-merker');
    merker.append(merke(k.project, 'prosjekt'));
    if (k.category) merker.append(merke(k.category));
    if (k.tilordnet) merker.append(merke(`→ ${k.tilordnet}`, 'person'));
    const sp = k.sprintId ? t.sprinter.find((s) => s.id === k.sprintId) : undefined;
    if (k.sprintId) merker.append(merke(sp ? sp.navn : `Sprint ${k.sprintId}`, 'sprint'));
    if (k.overstyrt) {
      // Selve poenget med overlegget: avviket mot VALE skal SYNES.
      const o = merke('Avviker fra VALE', 'gull');
      o.title = 'Kortet er flyttet til en annen kolonne enn den VALE genererte det i.';
      merker.append(o);
    }
    brikke.append(merker);

    const handlinger = div('kort-handlinger');
    handlinger.append(knapp('', () => visKortEndring(k, brikke), {
      variant: 'lenke', tittel: 'Endre kortet', ikon: ikon('grep'),
    }));
    brikke.append(handlinger);
    return brikke;
  }

  // ══ Tastaturflytting ══════════════════════════════════════════════════
  function tastetrykk(e: KeyboardEvent, k: Kort, per: PerKolonne, brikke: HTMLElement): void {
    if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      if (løftet === k.id) {
        løftet = null;
        brikke.classList.remove('løftet');
        ctx.kunngjør(`${k.title} sluppet i ${KOLONNE_NAVN[k.kolonne]}.`);
      } else {
        løftet = k.id;
        brikke.classList.add('løftet');
        ctx.kunngjør(`${k.title} løftet. Flytt med piltastene, slipp med Enter.`);
      }
      return;
    }
    if (e.key === 'Escape' && løftet === k.id) {
      løftet = null;
      brikke.classList.remove('løftet');
      ctx.kunngjør('Flyttingen avbrutt.');
      return;
    }
    if (løftet !== k.id) return;
    const retning = ({
      ArrowLeft: 'venstre', ArrowRight: 'høyre', ArrowUp: 'opp', ArrowDown: 'ned',
    } as const)[e.key];
    if (!retning) return;
    e.preventDefault();
    const flytting = flyttMedTastatur(per, k.id, retning);
    if (!flytting) return ctx.kunngjør('Kortet kan ikke flyttes lenger den veien.');
    void utfør(flytting, k.id);
  }

  /**
   * Utfør en flytting: optimistisk lokalt, så PATCH. Feiler kallet, HENTER vi
   * hele tavla på nytt framfor å rulle tilbake selv — tjenesten er fasiten, og
   * en lokal tilbakerulling kan bomme hvis noen andre har flyttet samtidig.
   */
  async function utfør(flytting: Flytting | null, fokusId?: string): Promise<void> {
    if (!flytting) return;
    const kortet = t.kort.find((x) => x.id === flytting.kortId);
    if (!kortet) return;
    const før = { kolonne: kortet.kolonne, rekkefolge: kortet.rekkefolge };
    kortet.kolonne = flytting.kolonne;
    kortet.rekkefolge = flytting.rekkefolge;
    tegnOversikt();
    tegnTavle();
    if (fokusId) fokuser(fokusId);
    ctx.kunngjør(`${kortet.title} flyttet til ${KOLONNE_NAVN[flytting.kolonne]}.`);

    const endring: KortEndring = { kolonne: flytting.kolonne, rekkefolge: flytting.rekkefolge };
    const r = await ctx.klient.endreKort(flytting.kortId, endring);
    if (r.ok) return;
    kortet.kolonne = før.kolonne;
    kortet.rekkefolge = før.rekkefolge;
    ctx.kvitter(`Flyttingen ble ikke lagret: ${r.feil.melding}`, 'feil');
    await hentAlt();
  }

  function fokuser(id: string): void {
    const el = tavleplass.querySelector<HTMLElement>(`.kort-brikke[data-id="${CSS.escape(id)}"]`);
    el?.focus();
  }

  // ══ Endre kort ════════════════════════════════════════════════════════
  function visKortEndring(k: Kort, brikke: HTMLElement): void {
    const fra = brikke.querySelector('.kort-endre');
    if (fra) return fra.remove();

    const boks = div('kort-endre');
    const personV = velger('Tilordnet', [
      { verdi: '', navn: 'Ingen' },
      ...t.personer.map((pid) => ({ verdi: pid, navn: pid })),
    ], k.tilordnet ?? '');
    boks.append(personV.rot);

    if (t.sprinterUtilgjengelig) {
      boks.append(p('felt-hjelp', `Sprint kan ikke velges nå: ${t.sprinterUtilgjengelig}`));
    } else {
      const sprV = velger('Sprint', [
        { verdi: '', navn: 'Ingen sprint' },
        ...t.sprinter.map((s) => ({ verdi: s.id, navn: s.navn })),
      ], k.sprintId ?? '');
      boks.append(sprV.rot);
      sprV.select.addEventListener('change', () => {
        void lagre({ sprintId: sprV.select.value });
      });
    }
    personV.select.addEventListener('change', () => {
      void lagre({ tilordnet: personV.select.value });
    });

    const fot = div('kort-endre-fot');
    if (kanSlettes(k)) {
      fot.append(knapp('Slett kortet', () => void slett(k), { variant: 'fare', ikon: ikon('kryss') }));
    } else {
      // Ingen knapp — bare forklaringen på hvorfor det ikke går, ordrett den
      // tjenesten ville gitt i sin 403.
      fot.append(p('felt-hjelp',
        'Kortet kommer fra VALE og kan ikke slettes her — flytt det til «Utgår» i stedet.'));
    }
    fot.append(knapp('Lukk', () => boks.remove(), { variant: 'stille' }));
    boks.append(fot);
    brikke.append(boks);
    personV.select.focus();

    async function lagre(endring: KortEndring): Promise<void> {
      const r = await ctx.klient.endreKort(k.id, endring);
      if (!r.ok) return ctx.kvitter(r.feil.melding, 'feil');
      ctx.kvitter('Kortet er oppdatert');
      await hentAlt();
    }
  }

  async function slett(k: Kort): Promise<void> {
    if (!globalThis.confirm(`Slette kortet «${k.title}»? Dette kan ikke angres.`)) return;
    const r = await ctx.klient.slettKort(k.id);
    if (!r.ok) return ctx.kvitter(r.feil.melding, 'feil');
    ctx.kvitter('Kortet er slettet');
    ctx.kunngjør(`Kortet ${k.title} er slettet.`);
    await hentAlt();
  }

  // ══ Nytt kort ═════════════════════════════════════════════════════════
  function visNyttKort(): void {
    if (nyttplass.firstChild) return sett(nyttplass);
    const kortEl = div('kort kort-skjema');
    const hode = div('kort-hode');
    hode.append(ikon('pluss'), h(2, 'kort-tittel', 'Nytt kort'));
    kortEl.append(hode);
    kortEl.append(p('kort-brød', 'Kortet opprettes i modulens eget lager. Det er ikke '
      + 'et VALE-kort og påvirker ikke `vale/data/kanban.json`.'));

    const skjema = document.createElement('form');
    skjema.className = 'skjema-rutenett';
    skjema.noValidate = true;

    const tittel = felt('Tittel', { påkrevd: true, plassholder: 'Hva skal gjøres?' });
    const prosjekt = felt('Prosjekt', { påkrevd: true, verdi: prosjektV.select.value || 'SKADE' });
    const kategori = felt('Kategori', { plassholder: 'valgfritt' });
    const kolonneV = velger('Kolonne', KANBAN_KOLONNER.map((k) => ({ verdi: k, navn: KOLONNE_NAVN[k] })), 'backlog');
    const notat = tekstområde('Notat', 'valgfritt', 3);
    const feilEl = div('skjema-feil');
    feilEl.setAttribute('role', 'alert');
    feilEl.hidden = true;

    const lag = knapp('Opprett kort', undefined, { variant: 'primær', type: 'submit' });
    const avbryt = knapp('Avbryt', () => sett(nyttplass), { variant: 'stille' });
    const fot = div('skjema-fot');
    fot.append(avbryt, lag);

    skjema.append(tittel.rot, prosjekt.rot, kategori.rot, kolonneV.rot);
    const bred = div('skjema-bred');
    bred.append(notat.rot);
    skjema.append(bred, fot, feilEl);
    kortEl.append(skjema);
    sett(nyttplass, kortEl);
    tittel.input.focus();

    skjema.addEventListener('submit', (e) => { e.preventDefault(); void opprett(); });

    async function opprett(): Promise<void> {
      tittel.settFeil(null);
      prosjekt.settFeil(null);
      feilEl.hidden = true;
      if (!tittel.input.value.trim()) {
        tittel.settFeil('Kortet må ha en tittel.');
        return tittel.input.focus();
      }
      if (!prosjekt.input.value.trim()) {
        prosjekt.settFeil('Oppgi hvilket prosjekt kortet hører til.');
        return prosjekt.input.focus();
      }
      const kropp: NyttKortKropp = {
        title: tittel.input.value.trim(),
        project: prosjekt.input.value.trim(),
        kolonne: kolonneV.select.value as KanbanKolonne,
      };
      const kat = kategori.input.value.trim();
      if (kat) kropp.category = kat;
      const n = notat.ta.value.trim();
      if (n) kropp.note = n;

      lag.disabled = true;
      const r = await ctx.klient.nyttKort(kropp);
      lag.disabled = false;
      if (!r.ok) {
        feilEl.hidden = false;
        feilEl.textContent = r.feil.melding;
        return;
      }
      sett(nyttplass);
      ctx.kvitter('Kortet er opprettet');
      ctx.kunngjør(`Kortet ${kropp.title} er opprettet i ${KOLONNE_NAVN[kropp.kolonne!]}.`);
      await hentAlt();
    }
  }
}
